/**
 * Feature Flag Definitions
 *
 * Single source of truth for all feature flags.
 * Adding a new flag is a one-line entry here — all admin UIs,
 * the Settings page, and Signup defaults derive from this array.
 *
 * See: docs/features/infrastructure/feature-access-matrix.md
 */

export const ROLE_HIERARCHY = ['viewer', 'clinician', 'standard', 'manager', 'companyAdmin', 'superAdmin'];

export const ROLE_LABELS = {
  viewer: 'Viewer',
  clinician: 'Clinician',
  standard: 'Standard',
  manager: 'Manager',
  companyAdmin: 'Company Admin',
  superAdmin: 'Super Admin',
};

export function meetsMinRole(userRole, minRole) {
  if (!minRole) return true; // no restriction
  return ROLE_HIERARCHY.indexOf(userRole || 'standard') >= ROLE_HIERARCHY.indexOf(minRole);
}

export const FEATURE_FLAG_DEFINITIONS = [
  {
    key: 'documentComments',
    label: 'Document Comments',
    description:
      'Inline margin comments on document sections, table rows, and highlighted text.',
    companyDefault: false,

    userSettingKey: 'useDocumentComments',
    userLabel: 'Document Comments',
    userTooltip:
      'Enable inline margin comments on documents and sections.',
    userDefault: false,
    showBetaBadge: true,
    settingsGroup: 'features',
    sortOrder: 27,

    hasSystemGate: true,
    minRole: 'clinician',
  },
  {
    key: 'useDarkMode',
    label: 'Dark Mode',
    description: 'Dark color theme for the entire application UI.',
    companyDefault: false,

    userSettingKey: 'useDarkMode',
    userLabel: 'Use Dark Mode',
    userTooltip: 'Enable dark mode theme for the application.',
    userDefault: false,
    showBetaBadge: true,
    settingsGroup: 'appearance',
    sortOrder: 30,

    hasSystemGate: false,
    minRole: null,
  },
  {
    key: 'showTags',
    label: 'Show Tags',
    description: 'Session tags displayed in document lists and patient views.',
    companyDefault: true,

    userSettingKey: 'showTags',
    userLabel: 'Show Tags',
    userTooltip: 'Show or hide session tags in document lists.',
    userDefault: false,
    showBetaBadge: false,
    settingsGroup: 'appearance',
    sortOrder: 10,

    hasSystemGate: false,
    minRole: null,
  },
  {
    key: 'showSeverity',
    label: 'Show Severity',
    description: 'Severity rankings, trends, and risk badges displayed in patient views.',
    companyDefault: true,

    userSettingKey: 'showSeverity',
    userLabel: 'Show Severity',
    userTooltip: 'Show severity rankings, trends, and risk badges in patient lists, History, and Overview.',
    userDefault: false,
    showBetaBadge: false,
    settingsGroup: 'appearance',
    sortOrder: 20,

    hasSystemGate: false,
    minRole: null,
  },
  {
    key: 'betaDocumentTypes',
    label: 'Beta Document Types',
    description: 'Experimental document types not yet promoted to the standard library.',
    companyDefault: false,

    userSettingKey: 'betaDocumentTypes',
    userLabel: 'Beta Document Types',
    userTooltip: 'Enable access to beta document types.',
    userDefault: false,
    showBetaBadge: true,
    settingsGroup: 'documents',
    sortOrder: 10,

    hasSystemGate: false,
    minRole: 'superAdmin',
  },
  {
    key: 'riskAssessment',
    label: 'Risk Assessment',
    description: 'Risk-assessment scoring and alerts during patient sessions.',
    companyDefault: true,

    userSettingKey: null,
    userDefault: null,

    hasSystemGate: true,
    minRole: null,
  },
  {
    key: 'advancedHistory',
    label: 'Advanced History',
    description:
      'Post-export patient History Summary generation. Summaries are produced from exported (reviewed) notes via the "History Summary" doc type, instead of inline during transcript processing.',
    companyDefault: false,

    userSettingKey: null,
    userDefault: null,

    hasSystemGate: true,
    minRole: 'clinician',
  },
  {
    key: 'chromeLogin',
    label: 'Google Login',
    description: 'Sign-in with Google accounts via Chrome browser authentication.',
    companyDefault: true,

    userSettingKey: null,
    userDefault: null,

    hasSystemGate: true,
    minRole: null,
  },
  {
    key: 'aiAssistant',
    label: 'AI Assistant',
    description:
      'AI-powered smart suggestions and quick actions within the document editor.',
    companyDefault: false,

    userSettingKey: 'useAiAssistant',
    userLabel: 'AI Assistant',
    userTooltip: 'Enable the AI assistant for smart suggestions and quick actions.',
    // AI is enabled in the live default user preset. Keep the code fallback in
    // sync so signup does not silently opt users out when templates are absent.
    userDefault: true,
    showBetaBadge: true,
    settingsGroup: 'features',
    sortOrder: 30,

    hasSystemGate: true,
    minRole: 'superAdmin',
  },
  {
    key: 'userSettingsMenu',
    label: 'User Settings Menu',
    description: 'Shows the Settings link in the user menu for managers and above.',
    companyDefault: true,

    userSettingKey: null,
    userDefault: null,

    hasSystemGate: false,
    minRole: 'manager',
  },
  {
    key: 'debugMode',
    label: 'Debug Mode',
    description:
      'Surfaces dev-only UI (e.g. recording-quality dots, extra console output, internal IDs) for troubleshooting.',
    companyDefault: false,

    userSettingKey: 'debugMode',
    userLabel: 'Debug Mode',
    userTooltip:
      'Show developer-only UI such as recording-quality indicators and internal IDs. Useful when reporting issues.',
    userDefault: false,
    settingsGroup: 'appearance',
    sortOrder: 90,

    hasSystemGate: false,
    minRole: 'superAdmin',
  },
];

// --- Computed helpers (derived from the array, not manually maintained) ---

/** Keyed lookup: flagsByKey.aiAssistant => definition object */
export const flagsByKey = Object.fromEntries(
  FEATURE_FLAG_DEFINITIONS.map((f) => [f.key, f]),
);

/** Flags that have a system-level gate (shown in Admin > System Settings) */
export const systemGatedFlags = FEATURE_FLAG_DEFINITIONS.filter(
  (f) => f.hasSystemGate,
);

/** Flags with a company layer (excludes systemOnly flags) */
export const companyFlags = FEATURE_FLAG_DEFINITIONS.filter(
  (f) => !f.systemOnly,
);

/** Flags with a user-facing toggle (shown in Settings page) */
export const userToggleFlags = FEATURE_FLAG_DEFINITIONS.filter(
  (f) => f.userSettingKey != null,
);

/** Default featureFlags map for new companies. */
export const defaultCompanyFeatureFlags = Object.fromEntries(
  FEATURE_FLAG_DEFINITIONS.map((f) => [f.key, f.companyDefault]),
);

/** Default flagMinRoles preset for new companies. */
export const defaultCompanyFlagMinRoles = Object.fromEntries(
  FEATURE_FLAG_DEFINITIONS.filter((f) => f.minRole != null).map((f) => [f.key, f.minRole]),
);

/** Default user settings entries for new signups. */
export const defaultUserSettings = Object.fromEntries(
  FEATURE_FLAG_DEFINITIONS.filter((f) => f.userSettingKey != null).map((f) => [
    f.userSettingKey,
    f.userDefault,
  ]),
);

/** Ordered groups for the Settings page */
export const SETTINGS_GROUPS = [
  { key: 'appearance', label: 'Appearance' },
  { key: 'documents', label: 'Documents' },
  { key: 'features', label: 'Features' },
];
